import React from 'react';
import { Checkbox } from '@bb-ui/react-library/dist/components/Checkbox';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import {
  ListItemButton,
  ListItemButtonProps,
} from '@bb-ui/react-library/dist/components/ListItemButton';
import {
  ExecutionDetailResponseBody,
  JobExecution,
  Status,
  TERMINAL_STATUSES,
} from '@lct-jobs/core';
import {
  ListItemIcon,
  PrimaryButton,
  createStyles,
  makeStyles,
  Typography,
} from '@bb-ui/react-library';
import { ListItemText } from '@bb-ui/react-library/dist/components/ListItemText';
import { Link, useHistory } from 'react-router-dom';
import { JobCardList } from './basic/JobCardList';
import { JobStatusIcon } from '../../JobStatusIcon/JobStatusIcon';
import { useTranslation } from 'react-i18next';
import { FAILED_JOB_STATUSES } from '../failedJobStatuses';
import { isReleaseStageExecution } from '../isReleaseStageExecution';
import { ReleaseStageUpgradeButtonDialog } from 'dialogs/ReleaseStageUpgradeButtonDialog';

const CHILD_JOBS_RENDER_BATCH_SIZE = 100;
const RELEASE_STAGE_UPGRADE_JOB_ID = 'UpgradeLearnInstance';
const TERMINAL_JOB_STATUSES: ReadonlySet<Status> = new Set(TERMINAL_STATUSES);

interface ListItemButtonLinkProps extends ListItemButtonProps {
  component?: React.ElementType;
  to?: string;
}

const ListItemButtonLink = ListItemButton as React.ElementType<ListItemButtonLinkProps>;

const useStyles = makeStyles((theme) =>
  createStyles({
    scrollableContainer: {
      maxHeight: 600,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    listItem: {
      paddingTop: 8,
      paddingBottom: 8,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
      '& + &': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    loadMoreContainer: {
      textAlign: 'center',
      marginTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    summaryBar: {
      display: 'flex',
      gap: theme.spacing(1.5),
      padding: '4px 16px 8px',
      flexWrap: 'wrap',
    },
    summaryChip: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.3px',
    },
    jobIdText: {
      fontSize: '13px',
    },
    filterControl: {
      marginLeft: 'auto',
      marginRight: 0,
      '& .MuiFormControlLabel-label': {
        fontSize: '13px',
      },
    },
    emptyMessage: {
      padding: '12px 16px',
      color: 'rgba(0, 0, 0, 0.6)',
    },
  }),
);

export interface JobChildrenProps {
  jobExecution?: ExecutionDetailResponseBody;
  detailsLoading?: boolean;
  siteDetailsLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  childLoadingError?: unknown;
}

const JobChildItem: React.FC<{
  child: JobExecution;
  className?: string;
  releaseStageExecution: boolean;
}> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const destination = `/tenants/${props.child.tenantId}/executions/${props.child.executionId}`;
  const jobName = props.child.jobName || props.child.jobId;

  if (props.releaseStageExecution) {
    return (
      <ListItemButtonLink
        component={Link}
        to={destination}
        className={props.className}
        aria-label={t('jobService.openChildExecution', {
          tenantId: props.child.tenantId,
          jobName,
          status: props.child.status,
        })}
      >
        <ListItemIcon>
          <JobStatusIcon status={props.child.status} />
        </ListItemIcon>
        <ListItemText
          primary={<span className={classes.jobIdText}>{props.child.tenantId}</span>}
          secondary={`${jobName} • ${props.child.status.toLowerCase().replace(/_/g, ' ')}`}
        />
      </ListItemButtonLink>
    );
  }

  return (
    <ListItemButton className={props.className} onClick={() => history.push(destination)}>
      <ListItemIcon>
        <JobStatusIcon status={props.child.status} />
      </ListItemIcon>
      <ListItemText
        primary={<span className={classes.jobIdText}>{`Job ID: ${props.child.jobId}`}</span>}
        secondary={props.child.status}
      />
    </ListItemButton>
  );
};

export const JobChildrenBox: React.FC<JobChildrenProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const execution = props.jobExecution;
  const loadingDetails = props.detailsLoading;
  const { siteDetailsLoading } = props;
  const children = React.useMemo(() => execution?.children || [], [execution?.children]);
  const releaseStageExecution = isReleaseStageExecution(execution);
  const releaseStageUpgradeExecution =
    releaseStageExecution && execution?.jobId === RELEASE_STAGE_UPGRADE_JOB_ID;
  const [failedOnly, setFailedOnly] = React.useState(false);
  const [renderLimit, setRenderLimit] = React.useState(CHILD_JOBS_RENDER_BATCH_SIZE);
  const [retryDialogOpen, setRetryDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setFailedOnly(false);
    setRenderLimit(CHILD_JOBS_RENDER_BATCH_SIZE);
    setRetryDialogOpen(false);
  }, [execution?.executionId]);

  const failedChildren = React.useMemo(
    () => children.filter((child) => FAILED_JOB_STATUSES.has(child.status)),
    [children],
  );
  const failedTenantIds = React.useMemo(
    () => Array.from(new Set(failedChildren.map((child) => child.tenantId))),
    [failedChildren],
  );
  const allChildExecutionsTerminal = children.every((child) =>
    TERMINAL_JOB_STATUSES.has(child.status),
  );
  const retryDisabled =
    Boolean(loadingDetails || siteDetailsLoading || props.hasMore || props.childLoadingError) ||
    !allChildExecutionsTerminal ||
    failedTenantIds.length === 0;
  const visibleChildren = releaseStageExecution && failedOnly ? failedChildren : children;
  const renderedChildren = releaseStageExecution
    ? visibleChildren.slice(0, renderLimit)
    : visibleChildren;
  const hasMoreRenderedChildren =
    releaseStageExecution && renderedChildren.length < visibleChildren.length;

  const handleFailedOnlyChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFailedOnly(checked);
    setRenderLimit(CHILD_JOBS_RENDER_BATCH_SIZE);
  };

  const handleLoadMore = () => {
    if (releaseStageExecution && hasMoreRenderedChildren) {
      setRenderLimit((currentLimit) => currentLimit + CHILD_JOBS_RENDER_BATCH_SIZE);
      return;
    }

    props.onLoadMore?.();
  };

  const statusCounts = React.useMemo(
    () =>
      children.reduce(
        (acc, child) => {
          const status = child.status || 'UNKNOWN';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
    [children],
  );

  let subtitle: string;
  if (!releaseStageExecution) {
    subtitle = `${children.length}${props.hasMore ? '+' : ''} total`;
  } else if (failedOnly || hasMoreRenderedChildren) {
    subtitle = t('jobService.childExecutionsFilteredCount', {
      shown: renderedChildren.length,
      total: `${visibleChildren.length}${props.hasMore ? '+' : ''}`,
    });
  } else {
    subtitle = t('jobService.childExecutionsTotal', {
      total: `${children.length}${props.hasMore ? '+' : ''}`,
    });
  }

  return (
    <>
      <JobCardList
        loading={loadingDetails || siteDetailsLoading}
        title={
          releaseStageExecution ? t('jobService.childExecutionsTitle') : 'Child Job Executions'
        }
        subtitle={subtitle}
        headerAction={
          releaseStageUpgradeExecution ? (
            <PrimaryButton
              disabled={retryDisabled}
              title={
                props.hasMore || !allChildExecutionsTerminal
                  ? t('jobService.retryFailedChildExecutionsLoading')
                  : undefined
              }
              onClick={() => setRetryDialogOpen(true)}
            >
              {t('jobService.retryFailedChildExecutions', { count: failedTenantIds.length })}
            </PrimaryButton>
          ) : undefined
        }
      >
        {children.length > 0 && (
          <div className={classes.summaryBar}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Typography
                key={status}
                className={classes.summaryChip}
                variant="body2"
                component="span"
              >
                {count} {status.toLowerCase().replace(/_/g, ' ')}
              </Typography>
            ))}
            {releaseStageExecution && (
              <FormControlLabel
                className={classes.filterControl}
                control={
                  <Checkbox
                    checked={failedOnly}
                    onChange={handleFailedOnlyChange}
                    inputProps={{ 'aria-label': t('jobService.failedChildExecutionsOnly') }}
                  />
                }
                label={t('jobService.failedChildExecutionsOnlyWithCount', {
                  count: failedChildren.length,
                })}
              />
            )}
          </div>
        )}
        <div className={classes.scrollableContainer}>
          {renderedChildren.map((child) => (
            <JobChildItem
              key={child.executionId}
              child={child}
              className={classes.listItem}
              releaseStageExecution={releaseStageExecution}
            />
          ))}
          {releaseStageExecution && failedOnly && visibleChildren.length === 0 && (
            <Typography className={classes.emptyMessage} variant="body2">
              {t('jobService.noFailedChildExecutions')}
            </Typography>
          )}
        </div>

        {(hasMoreRenderedChildren || props.hasMore) && (
          <div className={classes.loadMoreContainer}>
            <PrimaryButton onClick={handleLoadMore}>{t('global.loadMore')}</PrimaryButton>
          </div>
        )}
        {releaseStageUpgradeExecution && props.childLoadingError && (
          <Typography className={classes.emptyMessage} variant="body2" role="alert">
            {t('jobService.childExecutionsLoadFailed')}
          </Typography>
        )}
      </JobCardList>
      {releaseStageUpgradeExecution && execution && (
        <ReleaseStageUpgradeButtonDialog
          id={`retry-release-stage-upgrade-${execution.executionId}`}
          open={retryDialogOpen}
          onClose={() => setRetryDialogOpen(false)}
          releaseStageId={execution.tenantId}
          releaseStageName={execution.tenantId}
          initialTenantList={failedTenantIds}
          onSuccess={() => setRetryDialogOpen(false)}
        />
      )}
    </>
  );
};
